/**
 * ============================================================
 * ESENCIA — SISTEMA AUTOMÁTICO DE REGALOS V1
 * ============================================================
 *
 * REGLAS EXACTAS:
 *
 * 5 piezas reales  → Regalo Esencia 5
 * 10 piezas reales → Regalo Esencia 10
 * 15 piezas reales → Regalo Esencia 15
 *
 * Cualquier otra cantidad:
 * → NO recibe regalo.
 *
 * NO cuentan como piezas:
 * - Helpers de Selección especial +$70
 * - Regalos Esencia
 * - Otros elementos internos marcados
 *
 * Este archivo:
 * - NO modifica precios.
 * - NO modifica OC Quantity Breaks.
 * - NO modifica Selección especial.
 * - NO decide todavía si se puede ir a checkout.
 *
 * ============================================================
 */

(() => {

  'use strict';


  /* ============================================================
     EVITAR DOBLE INICIALIZACIÓN
     ============================================================ */

  if (window.__ESENCIA_GIFTS_V1__) {
    return;
  }

  window.__ESENCIA_GIFTS_V1__ = true;



  /* ============================================================
     CONFIGURACIÓN
     ============================================================ */

  const CONFIG = {

    helperHandle:
      'extra-por-seleccion-especial',

    gifts: {

      5: {
        handle:
          'regalo-esencia-parches-rhode-2-pares',

        label:
          'Regalo Esencia 5'
      },


      10: {
        handle:
          'regalo-esencia-10-2-pares-rhode-peptide-eye-prep-gel-bath-body-works',

        label:
          'Regalo Esencia 10'
      },


      15: {
        handle:
          'regalo-esencia-15-rhode-peptide-lip-tint-gel-bath-body-works',

        label:
          'Regalo Esencia 15'
      }

    }

  };



  const GIFT_HANDLES =
    new Set(
      Object.values(CONFIG.gifts)
        .map(gift => gift.handle)
    );


  const PRODUCT_CACHE =
    new Map();


  let syncTimer =
    null;


  let isSyncing =
    false;



  /* ============================================================
     UTILIDADES
     ============================================================ */

  function normalize(value) {

    return String(value || '')
      .trim()
      .toLowerCase();

  }


  function isTrue(value) {

    return (
      value === true ||
      normalize(value) === 'true'
    );

  }


  function getRoot() {

    const root =
      window.Shopify?.routes?.root ||
      '/';


    return root.endsWith('/')
      ? root
      : `${root}/`;

  }



  /* ============================================================
     IDENTIFICAR ELEMENTOS INTERNOS
     ============================================================ */

  function isGiftItem(item) {

    if (!item) {
      return false;
    }


    const handle =
      normalize(
        item.handle ||
        item.product_handle
      );


    if (
      GIFT_HANDLES.has(handle)
    ) {

      return true;
    }


    const properties =
      item.properties || {};


    if (
      isTrue(
        properties._esencia_gift
      )
    ) {

      return true;
    }


    return false;

  }



  function isSpecialHelper(item) {

    if (!item) {
      return false;
    }


    const handle =
      normalize(
        item.handle ||
        item.product_handle
      );


    if (
      handle ===
      normalize(CONFIG.helperHandle)
    ) {

      return true;
    }


    const properties =
      item.properties || {};


    if (
      isTrue(
        properties._esencia_extra_helper
      )
    ) {

      return true;
    }


    return false;

  }



  function isInternalItem(item) {

    if (!item) {
      return false;
    }


    if (
      isGiftItem(item) ||
      isSpecialHelper(item)
    ) {

      return true;
    }


    const properties =
      item.properties || {};


    if (
      isTrue(
        properties._esencia_internal
      )
    ) {

      return true;
    }


    return false;

  }



  /* ============================================================
     CONTAR PIEZAS REALES
     ============================================================ */

  function getRealQuantity(cart) {

    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {

      return 0;
    }


    return cart.items.reduce(
      (total, item) => {

        if (
          isInternalItem(item)
        ) {

          return total;
        }


        return (
          total +
          Number(
            item.quantity || 0
          )
        );

      },
      0
    );

  }



  /* ============================================================
     DETERMINAR REGALO CORRECTO
     ============================================================ */

  function getTargetGift(
    realQuantity
  ) {

    if (
      realQuantity === 5
    ) {

      return CONFIG.gifts[5];

    }


    if (
      realQuantity === 10
    ) {

      return CONFIG.gifts[10];

    }


    if (
      realQuantity === 15
    ) {

      return CONFIG.gifts[15];

    }


    /*
     * Cantidades inválidas:
     *
     * 0–4
     * 6–9
     * 11–14
     * 16+
     *
     * NO regalo.
     */

    return null;

  }



  /* ============================================================
     LEER CARRITO
     ============================================================ */

  async function getCart() {

    const response =
      await fetch(
        `${getRoot()}cart.js?esenciaGift=${Date.now()}`,
        {

          method:
            'GET',

          credentials:
            'same-origin',

          headers: {
            Accept:
              'application/json'
          }

        }
      );


    if (!response.ok) {

      throw new Error(
        `No se pudo leer el carrito: ${response.status}`
      );

    }


    return response.json();

  }



  /* ============================================================
     OBTENER PRODUCTO DE REGALO
     ============================================================ */

  async function getProduct(
    handle
  ) {

    if (!handle) {
      return null;
    }


    if (
      PRODUCT_CACHE.has(handle)
    ) {

      return PRODUCT_CACHE.get(handle);

    }


    const request =
      fetch(
        `${getRoot()}products/${encodeURIComponent(handle)}.js`,
        {

          method:
            'GET',

          credentials:
            'same-origin',

          headers: {
            Accept:
              'application/json'
          }

        }
      )
        .then(response => {

          if (!response.ok) {

            throw new Error(
              `Producto ${handle}: ${response.status}`
            );

          }


          return response.json();

        })
        .catch(error => {

          PRODUCT_CACHE.delete(handle);

          throw error;

        });


    PRODUCT_CACHE.set(
      handle,
      request
    );


    return request;

  }



  async function getGiftVariantId(
    gift
  ) {

    const product =
      await getProduct(
        gift.handle
      );


    if (
      !product ||
      !Array.isArray(product.variants) ||
      !product.variants.length
    ) {

      throw new Error(
        `El regalo ${gift.handle} no tiene una variante disponible.`
      );

    }


    /*
     * Preferimos una variante disponible.
     */

    const variant =
      product.variants.find(
        item =>
          item.available !== false
      ) ||
      product.variants[0];


    return variant.id;

  }



  /* ============================================================
     ELIMINAR / AJUSTAR REGALOS EXISTENTES
     ============================================================ */

  async function updateGiftLines(
    updates
  ) {

    if (
      !updates ||
      !Object.keys(updates).length
    ) {

      return;
    }


    const response =
      await fetch(
        `${getRoot()}cart/update.js`,
        {

          method:
            'POST',

          credentials:
            'same-origin',

          headers: {

            'Content-Type':
              'application/json',

            Accept:
              'application/json'

          },

          body:
            JSON.stringify({
              updates
            })

        }
      );


    if (!response.ok) {

      throw new Error(
        `No se pudieron actualizar los regalos: ${response.status}`
      );

    }


    return response.json();

  }



  /* ============================================================
     AGREGAR REGALO
     ============================================================ */

  async function addGift(
    gift,
    realQuantity
  ) {

    const variantId =
      await getGiftVariantId(
        gift
      );


    const response =
      await fetch(
        `${getRoot()}cart/add.js`,
        {

          method:
            'POST',

          credentials:
            'same-origin',

          headers: {

            'Content-Type':
              'application/json',

            Accept:
              'application/json'

          },

          body:
            JSON.stringify({

              items: [

                {

                  id:
                    variantId,

                  quantity:
                    1,

                  properties: {

                    _esencia_gift:
                      'true',

                    _esencia_gift_tier:
                      String(realQuantity),

                    _esencia_internal:
                      'true'

                  }

                }

              ]

            })

        }
      );


    if (!response.ok) {

      const errorText =
        await response.text();


      throw new Error(
        `No se pudo agregar ${gift.label}: ${errorText}`
      );

    }


    return response.json();

  }



  /* ============================================================
     SINCRONIZAR REGALO
     ============================================================ */

  async function syncGift() {

    if (isSyncing) {
      return;
    }


    isSyncing =
      true;


    let changed =
      false;


    try {

      const cart =
        await getCart();


      const realQuantity =
        getRealQuantity(cart);


      const targetGift =
        getTargetGift(
          realQuantity
        );


      const giftItems =
        cart.items.filter(
          isGiftItem
        );


      /*
       * ========================================================
       * CANTIDAD INVÁLIDA
       *
       * No debe existir ningún regalo.
       * ========================================================
       */

      if (!targetGift) {

        if (
          giftItems.length
        ) {

          const updates = {};


          giftItems.forEach(
            item => {

              updates[item.key] =
                0;

            }
          );


          await updateGiftLines(
            updates
          );


          changed =
            true;

        }


        return;

      }



      /*
       * ========================================================
       * CANTIDAD VÁLIDA
       *
       * Debe existir EXACTAMENTE
       * el regalo correspondiente.
       * ========================================================
       */

      let targetFound =
        false;


      const updates = {};


      giftItems.forEach(
        item => {

          const itemHandle =
            normalize(
              item.handle ||
              item.product_handle
            );


          /*
           * Es el regalo correcto.
           */

          if (
            itemHandle ===
            normalize(targetGift.handle) &&
            !targetFound
          ) {

            targetFound =
              true;


            /*
             * El regalo siempre debe tener cantidad 1.
             */

            if (
              Number(item.quantity) !== 1
            ) {

              updates[item.key] =
                1;

            }


            return;

          }


          /*
           * Regalo de otro nivel
           * o regalo duplicado.
           */

          updates[item.key] =
            0;

        }
      );


      if (
        Object.keys(updates).length
      ) {

        await updateGiftLines(
          updates
        );


        changed =
          true;

      }


      /*
       * No existe todavía el regalo correcto.
       */

      if (!targetFound) {

        await addGift(
          targetGift,
          realQuantity
        );


        changed =
          true;

      }


    } catch (error) {

      console.error(
        '[Esencia Gifts]',
        error
      );


    } finally {

      isSyncing =
        false;


      if (changed) {

        /*
         * Avisamos a los demás sistemas:
         * contador, resumen, helper, etc.
         */

        document.dispatchEvent(
          new CustomEvent(
            'esencia:gifts-updated'
          )
        );


        document.dispatchEvent(
          new CustomEvent(
            'esencia:cart-changed',
            {
              detail: {
                source:
                  'esencia-gifts'
              }
            }
          )
        );

      }

    }

  }



  /* ============================================================
     PROGRAMAR SINCRONIZACIÓN
     ============================================================ */

  function scheduleSync(
    delay = 120
  ) {

    if (
      syncTimer
    ) {

      clearTimeout(
        syncTimer
      );

    }


    syncTimer =
      window.setTimeout(
        () => {

          syncTimer =
            null;

          syncGift();

        },
        delay
      );

  }



  /* ============================================================
     EVENTOS
     ============================================================ */

  document.addEventListener(
    'esencia:cart-changed',
    event => {

      /*
       * Evitamos reaccionar inmediatamente
       * a nuestro propio evento.
       */

      if (
        event?.detail?.source ===
        'esencia-gifts'
      ) {

        return;

      }


      scheduleSync(
        180
      );

    }
  );


  document.addEventListener(
    'cart:updated',
    () => {

      scheduleSync(
        180
      );

    }
  );


  document.addEventListener(
    'cart:update',
    () => {

      scheduleSync(
        180
      );

    }
  );


  document.addEventListener(
    'shopify:section:load',
    () => {

      scheduleSync(
        100
      );

    }
  );


  window.addEventListener(
    'pageshow',
    () => {

      scheduleSync(
        100
      );

    }
  );



  /* ============================================================
     INICIO
     ============================================================ */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {

        scheduleSync(
          150
        );

      },
      {
        once:
          true
      }
    );


  } else {

    scheduleSync(
      150
    );

  }



  /* ============================================================
     API PARA PRUEBAS / FUTURO
     ============================================================ */

  window.EsenciaGifts = {

    sync() {

      scheduleSync(
        0
      );

    },


    async getStatus() {

      const cart =
        await getCart();


      const quantity =
        getRealQuantity(cart);


      return {

        realQuantity:
          quantity,

        validQuantity:
          [5, 10, 15].includes(
            quantity
          ),

        targetGift:
          getTargetGift(quantity),

        currentGifts:
          cart.items
            .filter(isGiftItem)
            .map(item => ({
              handle:
                item.handle,

              quantity:
                item.quantity
            }))

      };

    }

  };

})();